import React, { useState } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, Tooltip } from "@mui/material";
import { Edit, Delete, VerifiedUser } from "components/icons";

const getProficiencyColor = (level) => {
  const colors = {
    BEGINNER: "default",
    INTERMEDIATE: "info",
    ADVANCED: "warning",
    EXPERT: "success",
  };
  return colors[level] || "default";
};

export const MemberSkillsDataGrid = ({
  memberSkills,
  isLoading,
  onEdit,
  onDelete,
  onVerify,
  showActions = true,
  showVerifyAction = false,
}) => {
  const [pageSize, setPageSize] = useState(10);

  const getActions = (params) => {
    const actions = [];
    if (showVerifyAction && !params.row.isVerified) {
      actions.push(
        <Tooltip key="verify" title="Verify Skill">
          <IconButton onClick={() => onVerify(params.row)} color="success" size="small">
            <VerifiedUser />
          </IconButton>
        </Tooltip>
      );
    }
    if (showActions) {
      actions.push(
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>
      );
    }
    return actions;
  };

  const columns = [
    ...(showActions || showVerifyAction ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: getActions,
    }] : []),
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "skillName",
      headerName: "Skill",
      width: 180,
      valueGetter: (value, row) => row.skill?.skillName || "N/A",
    },
    {
      field: "category",
      headerName: "Category",
      width: 130,
      valueGetter: (value, row) => row.skill?.category?.replace("_", " ") || "N/A",
    },
    {
      field: "proficiencyLevel",
      headerName: "Proficiency",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getProficiencyColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "yearsOfExperience",
      headerName: "Experience",
      width: 100,
      valueFormatter: (value) => value ? `${value} yrs` : "N/A",
    },
    {
      field: "isPrimary",
      headerName: "Primary",
      width: 90,
      renderCell: (params) => params.value ? <Chip label="Yes" color="primary" size="small" /> : null,
    },
    {
      field: "isVerified",
      headerName: "Verified",
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <VerifiedUser /> : null}
          label={params.value ? "Verified" : "Pending"}
          color={params.value ? "success" : "warning"}
          size="small"
          variant={params.value ? "filled" : "outlined"}
        />
      ),
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={memberSkills || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id}
      />
    </MDBox>
  );
};

MemberSkillsDataGrid.propTypes = {
  memberSkills: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onVerify: PropTypes.func,
  showActions: PropTypes.bool,
  showVerifyAction: PropTypes.bool,
};

export default MemberSkillsDataGrid;

